import type { ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import type { ProfileRestrictionReason } from '@wlb/shared/utils/profileVisibility';
import { canViewField } from '@wlb/shared/utils/profileVisibility';
import type { Profile } from '@wlb/shared/types';
import { Avatar } from '../../components/ui/Avatar';
import { BuddiesIcon, LockIcon, AlertCircleIcon } from '../../components/ui/icons';
import { getProfileAvatarUrl } from './useCurrentProfile';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import './profile.css';

interface ProfileRestrictedProps {
  reason: ProfileRestrictionReason;
  profile?: Profile | null;
  actions?: ReactNode;
  /**
   * Tab strip shown between the identity card and the locked panel when the
   * viewer is a buddy — the profile keeps its normal structure while every
   * tab's content stays locked.
   */
  tabs?: ReactNode;
  /** Active tab key, used to wire the locked panel to the tab strip. */
  activeTabKey?: string;
}

const CONTENT = {
  private: { Icon: LockIcon, titleKey: 'profile.restricted.privateTitle', messageKey: 'profile.restricted.privateMessage' },
  buddiesOnly: { Icon: BuddiesIcon, titleKey: 'profile.restricted.buddiesTitle', messageKey: 'profile.restricted.buddiesMessage' },
  unavailable: { Icon: AlertCircleIcon, titleKey: 'profile.restricted.unavailableTitle', messageKey: 'profile.restricted.unavailableMessage' },
} as const;

export function ProfileRestricted({ reason, profile, actions, tabs, activeTabKey }: ProfileRestrictedProps) {
  const { t } = useTranslation();
  const { Icon, titleKey, messageKey } = CONTENT[reason];
  const username = profile ? getUsernameDisplay(profile) : '';
  const avatarUrl = profile && canViewField(profile.profilePicturePrivacy, { isBuddy: false })
    ? getProfileAvatarUrl(profile)
    : '';
  // Public stat, like Facebook's friend count on a locked profile.
  const buddiesCount = profile?.buddiesCount ?? 0;

  return (
    <div className="profile-restricted-layout">
      {profile && username && (
        <section className="profile-restricted-identity">
          <div className="profile-restricted-identity__main">
            {/* No `canonical`: a restricted response is not an authoritative
                avatar source (its URL is empty when the picture is private), so
                registering it would suppress valid cached avatars elsewhere. */}
            <Avatar
              src={avatarUrl}
              name={username}
              username={username}
              seed={profile.userGuid ?? profile.userId}
              profileUserGuid={profile.userGuid}
              userId={profile.userId}
              size="xl"
              square
            />
            <div className="profile-restricted-identity__text">
              <h2 className="profile-restricted-identity__name">{username}</h2>
              <p className="profile-restricted-identity__stat">
                <span className="profile-restricted-identity__stat-value">{buddiesCount}</span>
                {' '}
                {t('profile.infoCard.buddies')}
              </p>
            </div>
          </div>
          {actions && <div className="profile-card__actions">{actions}</div>}
        </section>
      )}

      {tabs}

      <section
        className="profile-restricted"
        role={tabs ? 'tabpanel' : undefined}
        id={tabs && activeTabKey ? `profile-tab-panel-${activeTabKey}` : undefined}
        aria-labelledby={tabs && activeTabKey ? `profile-tab-${activeTabKey}` : undefined}
      >
        <span className="profile-restricted__icon">
          <Icon size={24} />
        </span>
        <h2 className="profile-restricted__title">{t(titleKey)}</h2>
        <p className="profile-restricted__message">{t(messageKey)}</p>
      </section>
    </div>
  );
}
